import { cva, type VariantProps } from 'class-variance-authority';
import { Popover as PopoverPrimitive } from 'radix-ui';
import * as React from 'react';

import { cn } from '@/lib/utils';

import { XStack, YStack } from '../layout/layout';

export const CoachMarkRoot = PopoverPrimitive.Root;

export const CoachMarkAnchor = PopoverPrimitive.Anchor;

export const CoachMarkDismiss = PopoverPrimitive.Close;

const coachMarkContent = cva(
  cn(
    'z-50 rounded-xl p-4 outline-none',
    'text-foreground',
    'bg-surface border border-border-strong shadow-sm dark:shadow-md',
    'origin-[--radix-popover-content-transform-origin]',
    'motion-ease-out',
    'data-[state=open]:motion-duration-200',
    'data-[state=open]:motion-scale-in-95',
    'data-[state=open]:motion-opacity-in-0',
    'data-[state=closed]:motion-duration-150',
    'data-[state=closed]:motion-scale-out-95',
    'data-[state=closed]:motion-opacity-out-0',
    'coach-mark-content'
  ),
  {
    variants: {
      width: {
        sm: 'w-64',
        md: 'w-80',
        lg: 'w-[25rem]',
      },
    },
    defaultVariants: {
      width: 'md',
    },
  }
);

interface CoachMarkContentProps
  extends
    React.ComponentPropsWithoutRef<typeof PopoverPrimitive.Content>,
    VariantProps<typeof coachMarkContent> {
  /** Render the popover inside a portal. Default: `true` */
  portalled?: boolean;
  /** Hide the arrow pointing at the anchor. Default: `false` */
  hideArrow?: boolean;
}

export const CoachMarkContent = React.forwardRef<
  React.ElementRef<typeof PopoverPrimitive.Content>,
  CoachMarkContentProps
>(
  (
    {
      className,
      width,
      align = 'center',
      side = 'bottom',
      sideOffset = 10,
      portalled = true,
      hideArrow = false,
      onInteractOutside,
      children,
      ...props
    },
    ref
  ) => {
    const content = (
      <PopoverPrimitive.Content
        ref={ref}
        align={align}
        side={side}
        sideOffset={sideOffset}
        onInteractOutside={(event) => {
          // Coach marks are intentional; clicks elsewhere do not dismiss.
          event.preventDefault();
          onInteractOutside?.(event);
        }}
        className={cn(coachMarkContent({ width }), className)}
        {...props}
      >
        <YStack className="gap-1.5">{children}</YStack>
        {!hideArrow && (
          <PopoverPrimitive.Arrow
            width={14}
            height={7}
            className="fill-surface drop-shadow-[0_1px_0_var(--border-strong)]"
          />
        )}
      </PopoverPrimitive.Content>
    );

    return portalled ? (
      <PopoverPrimitive.Portal>{content}</PopoverPrimitive.Portal>
    ) : (
      content
    );
  }
);
CoachMarkContent.displayName = 'CoachMarkContent';

export const CoachMarkTitle = React.forwardRef<
  HTMLHeadingElement,
  React.HTMLAttributes<HTMLHeadingElement>
>(({ className, ...props }, ref) => (
  <h3
    ref={ref}
    className={cn(
      'text-sm font-semibold text-foreground text-balance leading-snug m-0',
      className
    )}
    {...props}
  />
));
CoachMarkTitle.displayName = 'CoachMarkTitle';

export const CoachMarkDescription = React.forwardRef<
  HTMLParagraphElement,
  React.HTMLAttributes<HTMLParagraphElement>
>(({ className, ...props }, ref) => (
  <p
    ref={ref}
    className={cn('text-sm text-foreground-muted leading-snug m-0', className)}
    {...props}
  />
));
CoachMarkDescription.displayName = 'CoachMarkDescription';

export const CoachMarkFooter = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement>
>(({ className, ...props }, ref) => (
  <XStack
    ref={ref}
    className={cn('items-center justify-end gap-2 pt-1', className)}
    {...props}
  />
));
CoachMarkFooter.displayName = 'CoachMarkFooter';
